import type { RecordStatus } from '@dyeatlas/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { FolderPlus, Inbox, Send, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { SaveToWorkspaceDialog } from '../workspaces/dialogs'
import { ReviewProgress } from './ReviewProgress'
import { Swatch } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { Card, EmptyState, LoadingPanel, Notice } from '@/components/ui/misc'
import { api, ApiError } from '@/lib/api'
import { cn, formatRelative } from '@/lib/utils'

interface WorkspaceRecord {
  id: string
  code: string
  kind: 'experiment' | 'reference'
  status: RecordStatus
  title: string
  swatchHex: string | null
  completionPct: number
  dyeSourceName: string | null
  updatedAt: string
  rejectionReason: string | null
}

export function RecordBucket({
  status,
  title,
  description,
}: {
  status: RecordStatus
  title: string
  description: string
}) {
  const queryClient = useQueryClient()
  const [confirming, setConfirming] = useState<WorkspaceRecord | null>(null)
  const [expanded, setExpanded] = useState<string | null>(null)
  const [projectRecordId, setProjectRecordId] = useState<string | null>(null)
  const [emptying, setEmptying] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['records', status],
    queryFn: () => api.get<{ records: WorkspaceRecord[] }>(`/records?status=${status}`),
  })

  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['records'] }),
      queryClient.invalidateQueries({ queryKey: ['workspace'] }),
      queryClient.invalidateQueries({ queryKey: ['browse'] }),
    ])

  const act = useMutation({
    mutationFn: ({ id, action }: { id: string; action: string }) =>
      action === 'delete' ? api.delete(`/records/${id}`) : api.post(`/records/${id}/${action}`),
    onSuccess: async (_result, variables) => {
      await invalidate()
      setConfirming(null)
      toast.success(
        {
          submit: 'Submitted for peer review. Reviewers have been notified.',
          withdraw: 'Withdrawn from review.',
          trash: 'Moved to Trash.',
          restore: 'Restored.',
          delete: 'Deleted permanently.',
        }[variables.action] ?? 'Done.',
      )
    },
    onError: (failure) =>
      toast.error(failure instanceof ApiError ? failure.message : 'That did not work.'),
  })

  const emptyTrash = useMutation({
    mutationFn: () => api.delete<{ deleted: number }>('/records/trash'),
    onSuccess: async ({ deleted }) => {
      await invalidate()
      setEmptying(false)
      toast.success(`Trash emptied. ${deleted} record${deleted === 1 ? '' : 's'} deleted permanently.`)
    },
    onError: (failure) =>
      toast.error(failure instanceof ApiError ? failure.message : 'That did not work.'),
  })

  const records = data?.records ?? []

  return (
    <>
      <header className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-semibold text-ink">{title}</h2>
          <p className="mt-0.5 text-[13px] text-ink-muted">{description}</p>
        </div>
        {status === 'trashed' && records.length > 0 && (
          <Button variant="secondary" size="sm" onClick={() => setEmptying(true)}>
            <Trash2 className="size-3.5" />
            Empty trash
          </Button>
        )}
      </header>

      {isLoading ? (
        <LoadingPanel label="Loading records" />
      ) : records.length === 0 ? (
        <EmptyState
          icon={<Inbox className="size-7" />}
          title={`Nothing in ${title.toLowerCase()}`}
          description={
            status === 'draft'
              ? 'Start a record and it will be saved here as you work.'
              : 'Records will appear here as they move through review.'
          }
          action={
            status === 'draft' ? (
              <Link to="/database/new">
                <Button>New record</Button>
              </Link>
            ) : undefined
          }
        />
      ) : (
        <div className="space-y-2">
          {records.map((record) => (
            <Card key={record.id} className="p-4">
              <div className="flex items-start gap-3">
                <Swatch hex={record.swatchHex} size="lg" className="rounded-[var(--radius-control)]" />

                <div className="min-w-0 flex-1">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-faint">
                    {record.code}
                  </p>
                  <h3 className="truncate font-display text-[15px] font-semibold text-ink">
                    {record.title || 'Untitled record'}
                  </h3>
                  <p className="mt-0.5 text-[12px] text-ink-muted">
                    {record.dyeSourceName ?? 'No dye source yet'} · edited{' '}
                    {formatRelative(record.updatedAt)}
                  </p>

                  {status === 'draft' && (
                    <div className="mt-2 flex items-center gap-2">
                      <div className="h-1.5 w-32 overflow-hidden rounded-full bg-line">
                        <div
                          className="h-full rounded-full bg-brand"
                          style={{ width: `${record.completionPct}%` }}
                        />
                      </div>
                      <span className="text-[11px] text-ink-faint">
                        {record.completionPct}% complete
                      </span>
                    </div>
                  )}

                  {status === 'in_review' && (
                    <button
                      type="button"
                      onClick={() =>
                        setExpanded((current) => (current === record.id ? null : record.id))
                      }
                      className="mt-2 text-[12px] font-semibold text-brand hover:underline"
                    >
                      {expanded === record.id ? 'Hide reviews' : 'See review progress'}
                    </button>
                  )}
                </div>

                <div className="flex shrink-0 items-center gap-2">
                  {status !== 'trashed' && record.kind === 'experiment' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      title="Move to a project"
                      onClick={() => setProjectRecordId(record.id)}
                    >
                      <FolderPlus className="size-3.5" />
                      Move to project
                    </Button>
                  )}

                  {['draft', 'finished', 'rejected'].includes(status) && (
                    <Link to={`/database/${record.id}/edit`}>
                      <Button variant="secondary" size="sm">
                        Edit
                      </Button>
                    </Link>
                  )}

                  {['finished', 'rejected'].includes(status) && (
                    <Button
                      size="sm"
                      loading={act.isPending}
                      onClick={() => act.mutate({ id: record.id, action: 'submit' })}
                    >
                      <Send className="size-3.5" />
                      {status === 'rejected' ? 'Resubmit' : 'Submit for review'}
                    </Button>
                  )}

                  {status === 'in_review' && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => act.mutate({ id: record.id, action: 'withdraw' })}
                    >
                      Withdraw
                    </Button>
                  )}

                  {status === 'trashed' ? (
                    <>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => act.mutate({ id: record.id, action: 'restore' })}
                      >
                        Restore
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setConfirming(record)}>
                        Delete
                      </Button>
                    </>
                  ) : (
                    status !== 'published' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => act.mutate({ id: record.id, action: 'trash' })}
                      >
                        Trash
                      </Button>
                    )
                  )}
                </div>
              </div>

              {status === 'rejected' && record.rejectionReason && (
                <div className={cn('mt-3')}>
                  <Notice tone="caution" title="Reviewer feedback">
                    {record.rejectionReason}
                  </Notice>
                </div>
              )}

              {expanded === record.id && (
                <div className="mt-3 border-t border-line pt-3">
                  <ReviewProgress recordId={record.id} />
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      <Dialog
        open={Boolean(confirming)}
        onOpenChange={(open) => !open && setConfirming(null)}
        size="sm"
        title="Delete permanently?"
        description={
          confirming
            ? `${confirming.code} and its photos will be removed for good. This cannot be undone.`
            : undefined
        }
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirming(null)}>
              Cancel
            </Button>
            <Button
              loading={act.isPending}
              onClick={() => confirming && act.mutate({ id: confirming.id, action: 'delete' })}
            >
              Delete permanently
            </Button>
          </>
        }
      >
        <p className="text-[13px] text-ink-soft">
          Restoring is no longer possible once a record is deleted.
        </p>
      </Dialog>

      <Dialog
        open={emptying}
        onOpenChange={setEmptying}
        size="sm"
        title="Empty trash?"
        description={`All ${records.length} record${records.length === 1 ? '' : 's'} in Trash and their photos will be removed for good. This cannot be undone.`}
        footer={
          <>
            <Button variant="secondary" onClick={() => setEmptying(false)}>
              Cancel
            </Button>
            <Button loading={emptyTrash.isPending} onClick={() => emptyTrash.mutate()}>
              Delete all permanently
            </Button>
          </>
        }
      >
        <p className="text-[13px] text-ink-soft">
          Restoring is no longer possible once a record is deleted.
        </p>
      </Dialog>

      <SaveToWorkspaceDialog
        recordId={projectRecordId}
        open={projectRecordId !== null}
        onOpenChange={(open) => !open && setProjectRecordId(null)}
      />
    </>
  )
}
